/**
 * "Record a voice note for this slide?" prompt.
 *
 * When a pathology case opens on the annotator's own in-progress submission
 * (and every time they move to another slide of it), a toast with a Start
 * button offers to begin a voice note. Nothing is recorded without that click,
 * so the microphone permission dialog only ever follows a user gesture and no
 * silent audio piles up. Each slide is offered once per visit: a dismissed or
 * expired prompt does not come back (Not now / 15 s timeout), and recording
 * via `V` or the Voice tab removes it. Not configurable by design: projects set the workflow.
 */
import {
  shouldPromptVoiceNote,
  VOICE_NOTE_PROMPT_DURATION_MS,
  VOICE_NOTE_PROMPT_TOAST_ID,
  type VoiceNotePromptConditions,
  voiceNotePromptKey
} from "@frontend/pages/case-workspace/pathology/lib/voice-note-prompt"
import { MicIcon } from "lucide-react"
import { useEffect, useRef } from "react"
import { toast } from "sonner"

/** Shows the prompt and returns a function that removes it. */
export type VoiceNotePromptPresenter = (onStart: () => void) => () => void

/**
 * Inverted surface (foreground background, background text) so this one
 * interactive toast reads as a different kind of object from the neutral
 * info toasts, without borrowing an accent stripe. Start is a plain primary
 * button; nothing here is an error, so no red. "Not now" (or the timeout)
 * drops it for this slide without recording.
 */
const presentWithToast: VoiceNotePromptPresenter = (onStart) => {
  toast("Record a voice note for this slide?", {
    id: VOICE_NOTE_PROMPT_TOAST_ID,
    duration: VOICE_NOTE_PROMPT_DURATION_MS,
    icon: <MicIcon className="size-4" />,
    classNames: {
      toast: "!bg-foreground !text-background !border-transparent !shadow-lg",
      title: "!text-sm !font-medium",
      actionButton: "!bg-primary !text-primary-foreground !font-medium",
      cancelButton: "!bg-transparent !text-background/70 hover:!text-background"
    },
    action: { label: "Start", onClick: onStart },
    cancel: { label: "Not now", onClick: () => {} }
  })
  return () => toast.dismiss(VOICE_NOTE_PROMPT_TOAST_ID)
}

interface UseVoiceNotePromptParams extends VoiceNotePromptConditions {
  start: () => Promise<void>
  /** Test seam; defaults to a sonner toast. */
  present?: VoiceNotePromptPresenter
}

export function useVoiceNotePrompt({
  caseId,
  activeSeriesId,
  canRecord,
  isViewerReady,
  status,
  unsupportedReason,
  start,
  present = presentWithToast
}: UseVoiceNotePromptParams): void {
  const promptedRef = useRef(new Set<string>())
  const startRef = useRef(start)
  const presentRef = useRef(present)
  useEffect(() => {
    startRef.current = start
  }, [start])
  useEffect(() => {
    presentRef.current = present
  }, [present])

  useEffect(() => {
    const conditions = { caseId, activeSeriesId, canRecord, isViewerReady, status, unsupportedReason }
    if (!shouldPromptVoiceNote(conditions)) return
    const key = voiceNotePromptKey(conditions.caseId, conditions.activeSeriesId)
    if (promptedRef.current.has(key)) return
    promptedRef.current.add(key)
    // The cleanup runs when the slide changes, the recorder leaves "idle"
    // (recording started another way) or the page unmounts.
    return presentRef.current(() => void startRef.current())
  }, [caseId, activeSeriesId, canRecord, isViewerReady, status, unsupportedReason])
}
